import React, { forwardRef, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { ZERO_VALUE, ONE_VALUE } from "@utils/constants";
import {
  FIRST_INDEX,
  SECOND_INDEX,
  THIRD_INDEX,
  FOURTH_INDEX,
  PAD_START,
  MONTH_SLICE_INDEX,
} from "./InputComponent.constants";
import { X } from "lucide-react";

const InputComponent = forwardRef(
  (
    {
      filter,
      handleChange,
      value,
      isNumber,
      isText,
      index,
      clearRange,
      selectedValues,
      hasRangeEnabled = true,
    },
    ref
  ) => {
    const { t: translate, i18n } = useTranslation();

    function formatTimeValue(timeStr) {
      // Extract hours, minutes, and seconds from the string
      const matches = timeStr.match(/(\d+)h (\d+)m (\d+)s/);
      if (!matches) return "";

      // Format into HH:MM or HH:MM:SS
      const hours = matches[ONE_VALUE].padStart(PAD_START, "0");
      const minutes = matches[THIRD_INDEX].padStart(PAD_START, "0");
      const seconds = matches[FOURTH_INDEX].padStart(PAD_START, "0");

      // Check if the input type="time" supports seconds
      const input = document.createElement("input");
      input.type = "time";
      input.value = "00:00:00";
      const supportsSeconds = input.value === "00:00:00";

      return supportsSeconds
        ? `${hours}:${minutes}:${seconds}`
        : `${hours}:${minutes}`;
    }

    function formatDateTimeLocalValue(dateTimeStr) {
      const date = new Date(dateTimeStr);
      const year = date.getFullYear();
      const month = (date.getMonth() + ONE_VALUE)
        .toString()
        .padStart(PAD_START, "0");
      const day = date.getDate().toString().padStart(PAD_START, "0");
      const hours = date.getHours().toString().padStart(PAD_START, "0");
      const minutes = date.getMinutes().toString().padStart(PAD_START, "0");

      const seconds = date.getSeconds().toString().padStart(PAD_START, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    const [hasRange, setHasRange] = useState(
      value?.value?.split("|").length > ONE_VALUE ? true : false
    );

    const valueToShow = !value
      ? ""
      : filter.inputType === "time"
      ? formatTimeValue(value)
      : filter.inputType === "datetime-local"
      ? formatDateTimeLocalValue(value)
      : value || filter.defaultValue || "";

    const mainPartValue = hasRange
      ? (!!valueToShow.value && valueToShow.value.split("|")[FIRST_INDEX]) || ""
      : valueToShow.value;
    let secondPartValue = hasRange
      ? (!!valueToShow.value && valueToShow.value.split("|")[SECOND_INDEX]) ||
        ""
      : "";

    const today = new Date();
    const formattedToday =
      filter.inputType === "month"
        ? today.toISOString().slice(ZERO_VALUE, MONTH_SLICE_INDEX)
        : today.toISOString().split("T")[ZERO_VALUE];

    return (
      <div key={filter.key} className=" items-center mb-3">
        {!!filter.name && (
          <label className="font-semibold text-sm dark:text-white text-gray-500 inline-flex rtl:text-right w-full">
            {filter.name}{" "}
            {filter.IsMandatory && <span className="text-red-500">{"*"}</span>}
          </label>
        )}
        {isNumber ? (
          <div className="relative">
            <input
              type="number"
              data-testid={filter.key}
              name={filter.key}
              placeholder={filter.placeHolder}
              value={value?.value || ""}
              onChange={(e) => handleChange(e, filter)}
              onBlur={(e) =>
                handleChange(e, filter, isNumber, filter.decimalPoint)
              }
              className={clsx(
                `border dark:bg-gray-600 dark:border-gray-500 border-gray-300 rounded-md p-2 w-full mt-2`,
                filter.prefix ? "pl-8" : "pl-4"
              )}
              onKeyDown={(e) => {
                if (!filter.hasDecimal && (e.key === "." || e.key === "e")) {
                  e.preventDefault();
                }
              }}
              min="0" // This sets the minimum value to 0
              step={filter.step}
              disabled={filter.disabled && filter.disabled(value?.value)}
              tabIndex={index}
            />
            <span className="absolute left-2 top-4">
              <span className="text-gray-500">{filter.prefix}</span>
            </span>
          </div>
        ) : isText ? (
          <div className="relative">
            <input
              type="text"
              name={filter.key}
              placeholder={filter.placeHolder}
              value={value?.value || ""}
              onChange={(e) => handleChange(e, filter)}
              className="border dark:bg-gray-600 dark:border-gray-500 border-gray-300 rounded-md p-2 w-full mt-2"
              tabIndex={index}
              disabled={filter.disabled && filter.disabled(value?.value)}
            />
          </div>
        ) : (
          <div className="relative">
            <div
              className={clsx(
                `flex items-center justify-between gap-4 mt-2`,
                hasRange && "pr-[36px]"
              )}
            >
              <input
                id="dateTimeInput"
                data-testid={filter.key}
                type={filter.inputType} //datetime-local, date, time
                name={filter.key}
                placeholder={filter.placeHolder}
                value={mainPartValue}
                onChange={(e) => handleChange(e, filter)}
                className="border dark:bg-gray-600 dark:border-gray-500 border-gray-300 rounded-md p-2 w-full"
                min={
                  filter.dynamicMinDate && filter.dynamicMinDate(selectedValues)
                }
                max={
                  hasRange && secondPartValue ? secondPartValue : formattedToday
                }
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                tabIndex={index}
                step={filter.step || ONE_VALUE}
                required={filter?.IsMandatory || false}
              />
              {hasRangeEnabled && !hasRange && (
                <button
                  disabled={!mainPartValue}
                  className={clsx(
                    `whitespace-nowrap dark:text-white`,
                    !mainPartValue &&
                      "dark:text-white/50 text-gray-500 cursor-not-allowed"
                  )}
                  onClick={() => setHasRange(true)}
                >
                  {translate("filters.add-range")}
                </button>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 mt-2 group">
              {hasRange && (
                <input
                  id="dateTimeInputRange"
                  type={filter.inputType} //datetime-local, date, time
                  name={filter.key}
                  placeholder={filter.placeHolder}
                  value={secondPartValue || ""}
                  onChange={(e) =>
                    handleChange(
                      e,
                      filter,
                      false,
                      ZERO_VALUE,
                      true,
                      valueToShow.value
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  className="border dark:bg-gray-600 dark:border-gray-500 border-gray-300 rounded-md p-2 w-full mt-2"
                  min={
                    filter.dynamicMinDate
                      ? filter.dynamicMinDate(selectedValues)
                      : mainPartValue
                  }
                  disabled={!mainPartValue}
                  max={formattedToday}
                  tabIndex={index}
                  step={filter.step || ONE_VALUE}
                />
              )}

              {hasRange && (
                <X
                  className="size-6 dark:text-white text-gray-400 cursor-pointer group-hover:text-red-500 hover:scale-125 hover:animate-pulse transition-all duration-700 ease-in-out"
                  onClick={() => {
                    handleChange(
                      { target: { value: mainPartValue, name: filter.key } },
                      filter,
                      false,
                      ZERO_VALUE,
                      false
                    );
                    setHasRange(false);
                    clearRange && clearRange();
                  }}
                  size={18}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

InputComponent.displayName = "InputComponent";

export default InputComponent;
